import { queryOptions, keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createScheduledJob,
  deleteScheduledJob,
  fetchScheduledJobs,
  pauseScheduledJob,
  resumeScheduledJob,
  runNowScheduledJob,
  updateScheduledJob,
  type CreateScheduledJobInput,
  type UpdateScheduledJobInput,
} from "@/features/scheduled-jobs/api/scheduled-jobs.api";

/** Cloud Scheduler state and next-run times move slowly; run history is what actually changes minute to minute. */
const SCHEDULED_JOBS_STALE_TIME_MS = 30 * 1000;

export const scheduledJobsKeys = {
  all: ["scheduled-jobs"] as const,
  list: (windowHours: number) => ["scheduled-jobs", "list", windowHours] as const,
} as const;

export function scheduledJobsQueryOptions(windowHours: number) {
  return queryOptions({
    queryKey: scheduledJobsKeys.list(windowHours),
    queryFn: () => fetchScheduledJobs(windowHours),
    staleTime: SCHEDULED_JOBS_STALE_TIME_MS,
    // Window flips must keep the last jobs on screen — otherwise the table and
    // chart blank for a frame while the next window loads (page flicker).
    placeholderData: keepPreviousData,
  });
}

function useInvalidateScheduledJobs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: scheduledJobsKeys.all });
}

export function useCreateScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: (input: CreateScheduledJobInput) => createScheduledJob(input),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpdateScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: ({ jobId, input }: { jobId: string; input: UpdateScheduledJobInput }) =>
      updateScheduledJob(jobId, input),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}

export function usePauseScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: (jobId: string) => pauseScheduledJob(jobId),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}

export function useResumeScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: (jobId: string) => resumeScheduledJob(jobId),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}

export function useDeleteScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: ({ jobId, confirmJobId }: { jobId: string; confirmJobId: string }) =>
      deleteScheduledJob(jobId, confirmJobId),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}

export function useRunNowScheduledJob() {
  const invalidate = useInvalidateScheduledJobs();

  return useMutation({
    mutationFn: (jobId: string) => runNowScheduledJob(jobId),
    onSuccess: () => {
      void invalidate();
    },
    meta: { skipGlobalError: true },
  });
}
